import {
  APIComponentAttributes,
  APIEntityType,
  APIReferenceAttributes,
  APIScopeData,
  AllDetailEntry,
  ArdoqId,
  ComponentTypeDetailEntry,
  SelectedDetailEntry,
  ViewpointDetailEntry,
  getEntityType,
  APIFieldAttributes,
} from '@ardoq/api-types';
import { EnhancedScopeData } from '@ardoq/data-model';
import { dateRangeOperations } from '@ardoq/date-range';
import {
  enhanceScopeData,
  getReferenceTypeByReferenceId,
} from '@ardoq/renderers';
import { uniq } from 'lodash';
import { fieldUtils } from '@ardoq/scope-data';
import { fieldOperations } from '@ardoq/core';

const defaultComponentAndReferenceAttributes = [
  'createdByEmail',
  'createdByName',
  'created',
  'lastModifiedByEmail',
  'lastModifiedByName',
  'lastUpdated',
];

export const defaultComponentAttributes = [
  'component-key',
  ...defaultComponentAndReferenceAttributes,
];

export const defaultReferenceAttributes = [
  'displayText',
  ...defaultComponentAndReferenceAttributes,
];

// The file type field is skipped because we can't match it to a name at this moment and don't want to always show Not Set, or it's id
const supportedByDrawer = (field: APIFieldAttributes) =>
  !fieldOperations.isFileField(field);

export const getFieldNames = (
  enhancedScopeData: EnhancedScopeData,
  detailEntry: SelectedDetailEntry | AllDetailEntry,
  entity: APIComponentAttributes | APIReferenceAttributes
) => {
  const dateRangeFieldMapForDetailEntry =
    enhancedScopeData.dateRangeFieldMap.get(entity.rootWorkspace) ?? {};

  return uniq(
    (detailEntry.selectedFields ?? []).map(detail =>
      dateRangeOperations.fieldNameIsPartOfDateRangeField(detail.name) &&
      dateRangeFieldMapForDetailEntry[
        dateRangeOperations.extractDateRangeFieldName(detail.name)
      ]
        ? dateRangeOperations.extractDateRangeFieldName(detail.name)
        : detail.name
    )
  );
};

const isComponentTypeDetailEntry = (
  detailEntry: ViewpointDetailEntry,
  entityType: APIEntityType
): detailEntry is ComponentTypeDetailEntry =>
  entityType === APIEntityType.COMPONENT && 'componentTypeName' in detailEntry;

const getTagsForEntity = (
  entityId: ArdoqId,
  scopeData: EnhancedScopeData,
  detailEntry?: ViewpointDetailEntry
) => {
  return detailEntry?.includeTags || !detailEntry
    ? scopeData.tags.filter(
        tag =>
          tag.components.includes(entityId) || tag.references.includes(entityId)
      )
    : [];
};

const getCustomFieldsSorted = (
  enhancedScopeData: EnhancedScopeData,
  entity: APIComponentAttributes | APIReferenceAttributes
) =>
  enhancedScopeData.fields
    .filter(field =>
      getEntityType(entity) === APIEntityType.COMPONENT
        ? fieldUtils.appliesToComponent(
            enhancedScopeData,
            field._id,
            entity._id
          )
        : fieldUtils.appliesToReference(
            enhancedScopeData,
            field._id,
            entity._id
          )
    )
    .filter(supportedByDrawer)
    .sort((a, b) => (a._order ?? Infinity) - (b._order ?? Infinity))
    .map(field => field.name);

export type FieldFilter = 'all' | 'default-only' | 'custom-only' | string[];

type GetRelevantFieldNamesArgs = {
  enhancedScopeData: EnhancedScopeData;
  entity: APIComponentAttributes | APIReferenceAttributes;
  entityType: APIEntityType;
  fieldFilter?: FieldFilter;
};

export const getRelevantFieldNames = ({
  enhancedScopeData,
  entity,
  entityType,
  fieldFilter,
}: GetRelevantFieldNamesArgs): string[] => {
  const defaultEntityAttributes =
    entityType === APIEntityType.COMPONENT
      ? defaultComponentAttributes
      : defaultReferenceAttributes;

  const customFields = getCustomFieldsSorted(enhancedScopeData, entity);

  if (!fieldFilter || fieldFilter === 'all') {
    return [...customFields, ...defaultEntityAttributes];
  }

  if (fieldFilter === 'default-only') {
    return defaultEntityAttributes;
  }

  if (fieldFilter === 'custom-only') {
    return customFields;
  }

  // fieldFilter is string[] - custom list of specific fields
  return fieldFilter;
};

type GetDetailsDataArgs = {
  scopeData: EnhancedScopeData;
  entity: APIComponentAttributes | APIReferenceAttributes;
  entityTypeName?: string;
  detailEntries?: ViewpointDetailEntry[];
  fieldFilter?: FieldFilter;
  includeTags?: boolean;
};

export const getDetailsData = ({
  scopeData,
  entity,
  entityTypeName,
  detailEntries,
  fieldFilter,
}: GetDetailsDataArgs) => {
  const entityType = getEntityType(entity)!;
  const enhancedScopeData = enhanceScopeData(
    dateRangeOperations.getMergedDateRangeFieldForScopeData(
      scopeData as unknown as APIScopeData
    ),
    { shouldFreeze: false }
  );

  const detailEntry = detailEntries?.find(detailEntry => {
    return isComponentTypeDetailEntry(detailEntry, entityType)
      ? detailEntry.componentTypeName === entityTypeName
      : detailEntry.referenceTypeName === entityTypeName;
  });

  const relevantFieldNames = getRelevantFieldNames({
    enhancedScopeData,
    entity,
    entityType,
    fieldFilter,
  });

  const tags = getTagsForEntity(entity._id, enhancedScopeData, detailEntry);

  if (!detailEntry) {
    const detailNames = uniq(relevantFieldNames);
    return { detailNames, enhancedScopeData, tags };
  }

  const detailNames = getSelectedDetails(
    enhancedScopeData,
    detailEntry,
    entity
  );

  const detailsToShow = detailNames.filter(name =>
    relevantFieldNames.includes(name)
  );

  return { detailNames: detailsToShow, enhancedScopeData, tags };
};

const getSelectedDetails = (
  enhancedScopeData: EnhancedScopeData,
  detailEntry: ViewpointDetailEntry,
  entity: APIComponentAttributes | APIReferenceAttributes
) => {
  const scope = detailEntry.scope;
  if (scope === 'all') {
    if (detailEntry.selectedFields?.length) {
      const selectedFields = detailEntry.selectedFields;
      const remainingFields = Object.keys(
        enhancedScopeData.fieldLabelByFieldName
      ).filter(
        fieldName => !selectedFields.some(field => field.name === fieldName)
      );
      return uniq([
        ...getFieldNames(enhancedScopeData, detailEntry, entity),
        ...remainingFields.sort(),
      ]);
    }
    return Object.keys(enhancedScopeData.fieldLabelByFieldName);
  }

  if (scope === 'none') return [];

  return getFieldNames(enhancedScopeData, detailEntry, entity);
};

export const getReferenceTypeName = (
  enhancedScopeData: EnhancedScopeData,
  reference: APIReferenceAttributes,
  parentChildReferenceName: string = 'Is parent of'
) => {
  if (reference.virtual) return parentChildReferenceName;
  const referenceType = getReferenceTypeByReferenceId(
    reference._id,
    enhancedScopeData
  );
  return referenceType?.name;
};

export const getBusinessCriticalFieldNames = (
  scopeData: EnhancedScopeData | null,
  businessCriticalFieldIds: string[],
  componentId: ArdoqId | null
): string[] | null => {
  if (!scopeData || !componentId) {
    return null;
  }
  const fieldNames = businessCriticalFieldIds
    .map(fieldId => scopeData.fieldsById[fieldId])
    .filter(
      field =>
        field &&
        fieldUtils.appliesToComponent(scopeData, field._id, componentId)
    )
    .sort((a, b) => (a._order ?? 0) - (b._order ?? 0))
    .map(field => field.name);
  return dateRangeOperations.mergeDateRangeFieldNames(fieldNames);
};
